/**
 * Where the flow stands on ONE ad platform (the ad-accounts step, J2). A single pure function, read by both the
 * transcript (which draws what each position means) and the Tasks panel (which reports it), so the chat and the
 * panel can never disagree about whether the step is waiting, docked or passed.
 */
import type {
  AdAccount,
  AdAccountsConfirmation,
  AdAccountsPlatform,
  AdPlatform,
  GetAdAccountsResponse,
  SessionSnapshot,
} from '@/contracts/onboarding-session';

import type { DataState } from './buildTranscript';
import { isTerminalState } from './jobState';
import type { OnboardingSessionState } from './session';

export type AdsStepPosition =
  /** The BFF cannot discover for this platform: no card, no step. */
  | { kind: 'unsupported' }
  /** The card shows and the step is not past yet: the accounts are loading, or the search has found none so far. */
  | { kind: 'waiting' }
  /** The card shows and the flow moves on without asking (the accounts failed to load, or Build closed the step). */
  | { kind: 'card-only' }
  /** The search ended with no account and nothing was confirmed: Scout says so and carries on. */
  | { kind: 'no-accounts' }
  /** The person confirmed (or skipped) this platform. */
  | { kind: 'confirmed'; confirmation: AdAccountsConfirmation; accounts: AdAccount[] }
  /** The confirm is docked, waiting on the person (`searching`: more accounts may still arrive). */
  | { kind: 'docked'; entry: AdAccountsPlatform; searching: boolean };

/** The accounts read for the platform, or null while they are not (idle, loading, failed). */
export function adsPlatformEntry(adData: DataState<GetAdAccountsResponse> | undefined, platform: AdPlatform): AdAccountsPlatform | null {
  return adData?.state === 'ready' ? (adData.data.platforms.find((p) => p.platform === platform) ?? null) : null;
}

export function adsStepPosition(
  session: Pick<OnboardingSessionState, 'completedAt'>,
  snapshot: SessionSnapshot | null,
  adData: DataState<GetAdAccountsResponse> | undefined,
  platform: AdPlatform,
): AdsStepPosition {
  const entry = adsPlatformEntry(adData, platform);
  if (adData?.state === 'ready' && (!entry || entry.status === 'unsupported')) return { kind: 'unsupported' };
  if (!adData || adData.state === 'idle' || adData.state === 'loading') return { kind: 'waiting' };
  // An error loading the accounts never blocks the flow: the confirm is left out (the card stays).
  if (!entry || entry.status === 'unsupported') return { kind: 'card-only' };

  const confirmation = snapshot?.confirmations.adAccounts[platform] ?? entry.confirmation;
  const searching = !isTerminalState(entry.status);
  // After Build the steps are closed: a read that failed earlier and succeeds later must not re-open one.
  if (session.completedAt !== null && !confirmation) return { kind: 'card-only' };
  if (entry.accounts.length === 0 && !searching && !confirmation) return { kind: 'no-accounts' };
  if (entry.accounts.length === 0 && searching && !confirmation) return { kind: 'waiting' };
  if (confirmation) return { kind: 'confirmed', confirmation, accounts: entry.accounts };
  return { kind: 'docked', entry, searching };
}
